import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import UnifiedSEO from '@/components/seo/UnifiedSEO';
import BreadcrumbNavigation from '@/components/seo/BreadcrumbNavigation';
import InternalLinkingOptimizer from '@/components/seo/InternalLinkingOptimizer';
import FeaturedPostCard from '@/components/blog/FeaturedPostCard';
import BlogPostCard from '@/components/blog/BlogPostCard';
import BlogSearch from '@/components/blog/BlogSearch';
import CategoryFilter from '@/components/blog/CategoryFilter';
import BlogContent from '@/components/blog/BlogContent';
import BlogPagination from '@/components/blog/BlogPagination';

import SocialShare from '@/components/blog/SocialShare';
import { Button } from '@/components/ui/button';
import { useSupabaseBlogPosts, useSupabaseBlogCategories } from '@/hooks/useSupabaseBlog';
import { transformSupabasePost } from '@/hooks/useBlog';
import CollectionPageSchema from '@/components/seo/CollectionPageSchema';
import BreadcrumbListSchema from '@/components/seo/BreadcrumbListSchema';
const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const {
    data: categoriesData
  } = useSupabaseBlogCategories();
  const {
    data: postsResponse,
    isLoading,
    error,
    refetch
  } = useSupabaseBlogPosts({
    page: currentPage,
    per_page: 13, // Fetch 13 posts: 1 featured + 12 regular
    search: searchTerm || undefined,
    categories: selectedCategory !== 'All' ? selectedCategory : undefined,
    orderby: 'published_at',
    order: 'desc'
  });
  const categories = ['All', ...(categoriesData?.map(cat => cat.name) || [])];
  const posts = postsResponse?.posts ? postsResponse.posts.map(transformSupabasePost) : [];
  const totalCount = postsResponse?.totalCount || 0;
  const featuredPost = posts[0]; // Use the most recent post as featured
  const regularPosts = posts.slice(1);
  
  // Calculate total pages based on actual total count
  const totalPages = Math.ceil(totalCount / 13);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [searchTerm, selectedCategory, currentPage, setSearchParams]);
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page on category change
  };
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate all blog-related queries to force fresh fetch
      await queryClient.invalidateQueries({
        queryKey: ['supabase-blog-posts', 'v2']
      });
      await queryClient.invalidateQueries({
        queryKey: ['supabase-blog-categories', 'v2']
      });
      await refetch();
      console.log('🔄 Blog content refreshed manually');
    } catch (error) {
      console.error('❌ Failed to refresh blog content:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  return <>
      <UnifiedSEO 
        title="Blog - Expert Advice for Healthy Aging | SeniorThrive" 
        description="Read expert advice, tips, and insights on healthy aging, wellness, community building, and making the most of your golden years." 
        keywords="senior blog, aging advice, wellness tips, health articles, retirement lifestyle, healthy aging" 
        canonical="https://seniorthrive.com/blog"
      />
      
      <BreadcrumbNavigation />
      <CollectionPageSchema
        name="SeniorThrive Blog"
        description="Expert advice, tips, and insights on healthy aging, wellness, community building, and making the most of your golden years."
        url="https://seniorthrive.com/blog"
        totalItems={totalCount}
      />
      <BreadcrumbListSchema
        breadcrumbs={[
          { name: 'Home', url: 'https://seniorthrive.com/' },
          { name: 'Blog', url: 'https://seniorthrive.com/blog' },
        ]}
      />
      
      <div className="py-20 px-4">
        <div className="container mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center items-center gap-4 mb-4">
              <h1 className="text-5xl font-bold text-foreground">The SeniorThrive Blog</h1>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing || isLoading} className="flex items-center gap-2">
                <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Expert advice, inspiring stories, and practical tips to help you 
              thrive in your golden years.
            </p>
          </div>

          <BlogSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />

          {/* Featured Post */}
          {!isLoading && featuredPost && <div className="mb-16">
              <h2 className="text-2xl font-bold text-foreground mb-8">Featured Article</h2>
              <FeaturedPostCard post={featuredPost} />
            </div>}

          <CategoryFilter categories={categories} selectedCategory={selectedCategory} onCategorySelect={handleCategoryChange} />

          <BlogContent posts={regularPosts} isLoading={isLoading} error={error} searchTerm={searchTerm} selectedCategory={selectedCategory} onClearFilters={handleClearFilters} />

          {/* Pagination */}
          {posts.length > 0 && <BlogPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}

          {/* Social Follow */}
          <div className="mt-16">
            <SocialShare title="" url="" variant="follow" />
          </div>

          
          {/* Internal Linking */}
          <div className="mt-16">
            <InternalLinkingOptimizer currentPage="blog" />
          </div>
        </div>
      </div>
    </>;
};
export default Blog;